import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const getCSSVar = (name: string, fallback: string): string => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name);
  return value.trim() || fallback;
};

const baseTheme = createTheme({
  shape: {
    borderRadius: 0, // ✅ Global square corners
  },
  palette: {
    primary: {
      main: getCSSVar('--primary-color', '#c4262e'),
      light: getCSSVar('--primary-color-light', '#e04a50'),
      contrastText: getCSSVar('--white', '#ffffff'),
    },
    secondary: {
      main: getCSSVar('--dark-gray', '#2d363a'),
    },
    background: {
      default: getCSSVar('--background-color', '#ffffff'),
      paper: getCSSVar('--header-bg', '#f0f0f0'),
    },
    text: {
      primary: getCSSVar('--text-color', '#222222'),
      secondary: getCSSVar('--text-secondary', '#777777'),
    },
    divider: getCSSVar('--divider-color', '#cccccc'),
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13, // Slightly smaller base
    h1: { fontSize: 'clamp(1.25rem, 3vw, 2rem)', fontWeight: 400 },
    h2: { fontSize: 'clamp(1.125rem, 2.5vw, 1.75rem)', fontWeight: 400 },
    h3: { fontSize: 'clamp(1rem, 2vw, 1.5rem)', fontWeight: 400 },
    h4: { fontSize: 'clamp(0.875rem, 1.75vw, 1.25rem)', fontWeight: 400 },
    h5: { fontSize: 'clamp(0.75rem, 1.5vw, 1rem)', fontWeight: 400 },
    h6: { fontSize: 'clamp(0.75rem, 1.25vw, 0.875rem)', fontWeight: 400 },
    body1: { fontSize: 'clamp(0.75rem, 1.25vw, 0.875rem)' },
    body2: { fontSize: 'clamp(0.675rem, 1.2vw, 0.825rem)' },
    button: { fontSize: 'clamp(0.7rem, 1vw, 0.875rem)', textTransform: 'none' },
    caption: { fontSize: 'clamp(0.625rem, 1vw, 0.75rem)' },
    subtitle1: { fontSize: 'clamp(0.75rem, 1.25vw, 0.875rem)', fontWeight: 300 },
    subtitle2: { fontSize: 'clamp(0.675rem, 1vw, 0.8rem)', fontWeight: 300 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: '4px 8px',
          minHeight: '32px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
        notchedOutline: {
          borderRadius: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '4px 8px',
          fontSize: '0.75rem',
          lineHeight: 0,
           height: '10px'
        },
        head: {
          fontWeight: 400,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: '0px', // override default row height
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            borderRadius: 0,
            fontSize: 'clamp(0.75rem, 1.25vw, 0.875rem)',
            minHeight: '36px',
          },
          '& .MuiInputLabel-root': {
            fontSize: 'clamp(0.75rem, 1.25vw, 0.875rem)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          height: 'clamp(22px, 5vw, 28px)',
          fontSize: 'clamp(0.7rem, 1vw, 0.825rem)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: '4px',
          borderRadius: 0,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 400,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

const theme = responsiveFontSizes(baseTheme, {
  factor: 2,
  breakpoints: ['sm', 'md', 'lg'],
});

export default theme;
